import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  Brain, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  TrendingUp,
  TrendingDown,
  Activity,
  Target,
  Lightbulb,
  Shield,
  AlertCircle,
  Search,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const AIValidation = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [timeEntries, setTimeEntries] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [selectedTimeEntry, setSelectedTimeEntry] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [retraining, setRetraining] = useState(false);
  const [activeTab, setActiveTab] = useState('time-tracking');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(10);
  
  // Search state — UI updates immediately; API uses debounced value (single fetch path)
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Individual validation loading states
  const [validatingEntries, setValidatingEntries] = useState(new Set());
  const [validatingTasks, setValidatingTasks] = useState(new Set());

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(searchQuery), 500);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  // Reset to first page when switching tabs or changing search
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, debouncedSearch]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const endpoint = activeTab === 'time-tracking' ? '/api/time-entries' : '/api/tasks';
        const response = await axios.get(endpoint, {
          params: {
            search: debouncedSearch || undefined,
          },
        });
        const { results, total } = response.data;
        if (activeTab === 'time-tracking') {
          setTimeEntries(results);
        } else {
          setTasks(results);
        }
        setTotalItems(total);
        setTotalPages(Math.ceil(total / itemsPerPage));
      } catch (error) {
        console.error('Error fetching AI validation data:', error);
        toast.error(
          activeTab === 'time-tracking'
            ? 'Failed to fetch time entries'
            : 'Failed to fetch tasks'
        );
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [activeTab, debouncedSearch, itemsPerPage]);

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const validateTimeEntry = async (timeEntryId) => {
    // Set individual entry as validating
    setValidatingEntries(prev => new Set(prev).add(timeEntryId));
    
    try {
      const response = await axios.post('/api/ai-validation/time-entry', {
        time_entry_id: timeEntryId
      });
      setValidationResults(response.data);
      toast.success('Time entry validated successfully');
    } catch (error) {
      toast.error('Failed to validate time entry');
    } finally {
      // Remove from validating set
      setValidatingEntries(prev => {
        const newSet = new Set(prev);
        newSet.delete(timeEntryId);
        return newSet;
      });
    }
  };

  const validateTaskEstimation = async (taskId, estimatedHours) => {
    // Set individual task as validating
    setValidatingTasks(prev => new Set(prev).add(taskId));
    
    try {
      const response = await axios.post('/api/ai-validation/task-estimation', {
        task_id: taskId,
        estimated_hours: estimatedHours
      });
      setValidationResults(response.data);
      toast.success('Task estimation validated successfully');
    } catch (error) {
      toast.error('Failed to validate task estimation');
    } finally {
      // Remove from validating set
      setValidatingTasks(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskId);
        return newSet;
      });
    }
  };

  const retrainModels = async () => {
    setRetraining(true);
    try {
      const response = await axios.post('/api/ai-validation/retrain-models');
      toast.success('AI models retrained successfully');
      
    } catch (error) {
      toast.error('Failed to retrain AI models');
    } finally {
      setRetraining(false);
    }
  };

  const getValidationIcon = (isValid, confidenceScore) => {
    if (confidenceScore > 0.8) {
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    } else if (confidenceScore > 0.5) {
      return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
    } else {
      return <AlertCircle className="h-5 w-5 text-red-500" />;
    }
  };

  const getRiskLevelColor = (riskLevel) => {
    switch (riskLevel) {
      case 'low': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'high': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const getCurrentPageItems = (items) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return items.slice(startIndex, endIndex);
  };

  const renderPagination = () => {
    if (totalPages <= 1 || totalItems === 0) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-between mt-6">
        <div className="text-sm text-gray-700">
          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} results
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          
          {pages.map(page => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`px-3 py-2 text-sm font-medium rounded-md ${
                page === currentPage
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {page}
            </button>
          ))}
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full min-w-0 max-w-none px-4 py-8">
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">AI Validation Dashboard</h1>
            <p className="text-gray-600">
              Use AI-powered validation to ensure accurate time tracking and task estimations
            </p>
          </div>
          <button
            onClick={retrainModels}
            disabled={retraining}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
          >
            <Brain className="h-4 w-4" />
            {retraining ? 'Retraining...' : 'Retrain Models'}
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => {
              setActiveTab('time-tracking');
            }}
            className={`px-3 py-2 rounded-md text-sm font-medium ${
              activeTab === 'time-tracking'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Clock className="h-4 w-4 inline mr-2" />
            Time Tracking Validation
          </button>
          <button
            onClick={() => {
              setActiveTab('task-estimation');
            }}
            className={`px-3 py-2 rounded-md text-sm font-medium ${
              activeTab === 'task-estimation'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Target className="h-4 w-4 inline mr-2" />
            Task Estimation Validation
          </button>
        </nav>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Panel - Data Selection */}
        <div className="space-y-6">
          {activeTab === 'time-tracking' ? (
            <div>
              <h2 className="text-xl font-semibold mb-4">Select Time Entry to Validate</h2>
              
              {/* Search Bar */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search time entries..."
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-500">Loading time entries...</p>
                </div>
              ) : (
                <>
                  {timeEntries.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No time entries found.
                    </div>
                  ) : (
                    <>
                      <div className="space-y-3">
                        {getCurrentPageItems(timeEntries).map((entry) => (
                          <div
                            key={entry.id}
                            className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                              selectedTimeEntry?.id === entry.id
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => {
                              setSelectedTimeEntry(entry);
                              navigate(`/time-entries/${entry.id}`);
                            }}
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <h3 className="font-medium text-gray-900">{entry.description}</h3>
                                <p className="text-sm text-gray-500">
                                  {new Date(entry.start_time).toLocaleDateString()} - {formatDuration(entry.duration)}
                                </p>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  validateTimeEntry(entry.id);
                                }}
                                disabled={validatingEntries.has(entry.id)}
                                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                              >
                                {validatingEntries.has(entry.id) ? 'Validating...' : 'Validate'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Pagination */}
                      {renderPagination()}
                    </>
                  )}
                </>
              )}
            </div>
          ) : (
            <div>
              <h2 className="text-xl font-semibold mb-4">Select Task to Validate Estimation</h2>
              
              {/* Search Bar */}
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search tasks..."
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-500">Loading tasks...</p>
                </div>
              ) : (
                <>
                  {tasks.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No tasks found.
                    </div>
                  ) : (
                    <>
                      <div className="space-y-3">
                        {getCurrentPageItems(tasks).map((task) => (
                          <div
                            key={task.id}
                            className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                              selectedTask?.id === task.id
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => {
                              setSelectedTask(task);
                              navigate(`/tasks/${task.id}`);
                            }}
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <h3 className="font-medium text-gray-900">{task.name}</h3>
                                <p className="text-sm text-gray-500">
                                  Estimated: {task.estimated_hours || 'Not set'} hours
                                </p>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (task.estimated_hours) {
                                    validateTaskEstimation(task.id, task.estimated_hours);
                                  } else {
                                    toast.error('Task has no estimated hours');
                                  }
                                }}
                                disabled={validatingTasks.has(task.id) || !task.estimated_hours}
                                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                              >
                                {validatingTasks.has(task.id) ? 'Validating...' : 'Validate'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Pagination */}
                      {renderPagination()}
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Right Panel - Validation Results */}
        <div className="space-y-6">
          <h2 className="text-xl font-semibold mb-4">AI Validation Results</h2>
          
          {validationResults ? (
            <div className="space-y-6">
              {/* Summary Card */}
              <div className="bg-white p-6 rounded-lg border shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium">Validation Summary</h3>
                  {getValidationIcon(validationResults.is_valid, validationResults.confidence_score)}
                </div>
                
                <div className="grid grid-cols-1 xs:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-gray-500">Confidence Score</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {(validationResults.confidence_score * 100).toFixed(1)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Anomaly Score</p>
                    <p className="text-2xl font-bold text-red-600">
                      {(validationResults.anomaly_score * 100).toFixed(1)}%
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Risk Level:</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskLevelColor(validationResults.risk_level)}`}>
                    {validationResults.risk_level.toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Warnings */}
              {validationResults.warnings && validationResults.warnings.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="h-5 w-5 text-yellow-600" />
                    <h3 className="font-medium text-yellow-800">Warnings</h3>
                  </div>
                  <ul className="space-y-2">
                    {validationResults.warnings.map((warning, index) => (
                      <li key={index} className="text-sm text-yellow-700 flex items-start gap-2">
                        <span className="w-1 h-1 bg-yellow-600 rounded-full mt-2 flex-shrink-0"></span>
                        {warning}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Suggestions */}
              {validationResults.suggestions && validationResults.suggestions.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Lightbulb className="h-5 w-5 text-blue-600" />
                    <h3 className="font-medium text-blue-800">Suggestions</h3>
                  </div>
                  <ul className="space-y-2">
                    {validationResults.suggestions.map((suggestion, index) => (
                      <li key={index} className="text-sm text-blue-700 flex items-start gap-2">
                        <span className="w-1 h-1 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Factors */}
              {validationResults.factors && validationResults.factors.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Activity className="h-5 w-5 text-gray-600" />
                    <h3 className="font-medium text-gray-800">Analysis Factors</h3>
                  </div>
                  <ul className="space-y-2">
                    {validationResults.factors.map((factor, index) => (
                      <li key={index} className="text-sm text-gray-700 flex items-start gap-2">
                        <span className="w-1 h-1 bg-gray-600 rounded-full mt-2 flex-shrink-0"></span>
                        {factor}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <Brain className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Validation Results</h3>
              <p className="text-gray-500">
                Select a {activeTab === 'time-tracking' ? 'time entry' : 'task'} and click validate to see AI-powered insights.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* AI Insights Section */}
      <div className="mt-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">AI-Powered Insights</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg border shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp className="h-6 w-6 text-green-600" />
              <h3 className="text-lg font-medium">Productivity Patterns</h3>
            </div>
            <p className="text-gray-600 text-sm">
              AI analyzes your time tracking patterns to identify peak productivity hours and suggest optimal work schedules.
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-lg border shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="h-6 w-6 text-blue-600" />
              <h3 className="text-lg font-medium">Anomaly Detection</h3>
            </div>
            <p className="text-gray-600 text-sm">
              Automatically detect unusual time entries, overlapping sessions, and potential tracking errors.
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-lg border shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <Target className="h-6 w-6 text-purple-600" />
              <h3 className="text-lg font-medium">Estimation Accuracy</h3>
            </div>
            <p className="text-gray-600 text-sm">
              Improve task estimation accuracy by learning from historical data and similar tasks.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIValidation; 